import Card, { CardDeadEndHint } from '@/components/Card';
import { ConditionalRender } from '@/components/ui/ConditionalRender';
import { Hint } from '@/components/ui/Hint';
import { useStepper } from '@/features/v2/lib/stepper';
import { ComplementarySurveyThree } from '@/features/v2/vehicle-damage/complementaryInformation/survey/ComplementarySurveyThree';
import { InputVehicleSoldPrice } from '@/features/v2/vehicle-damage/components/InputVehicleSoldPrice';
import {
  ACCIDENT_IN_POLAND_NAME_COMPLEMENTARY,
  RadioGroupAccidentInPoland,
} from '@/features/v2/vehicle-damage/components/RadioGroupAccidentInPoland';
import {
  ACCIDENT_REPORTED_TO_INSURER_NAME_COMPLEMENTARY,
  ACCIDENT_REPORTED_TO_INSURER_VALUES,
  RadioGroupAccidentReportedToInsurer,
} from '@/features/v2/vehicle-damage/components/RadioGroupAccidentReportedToInsurer';
import {
  AGREEMENT_OR_SETTLEMENT,
  AGREEMENT_OR_SETTLEMENT_NAME_COMPLEMENTARY,
  RadioGroupAgreementOrSettlement,
} from '@/features/v2/vehicle-damage/components/RadioGroupAgreementOrSettlement';
import {
  INSURER_PAY_COMPENSATION_NAME_COMPLEMENTARY,
  RadioGroupInsurerPayCompensation,
} from '@/features/v2/vehicle-damage/components/RadioGroupInsurerPayCompensation';
import {
  RadioGroupVehicleValueBeforeCrash,
  VEHICLE_VALUE_BEFORE_CRASH,
  VEHICLE_VALUE_BEFORE_CRASH_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioGroupVehicleValueBeforeCrash';
import {
  RadioGroupWheelSide,
  WHEEL_SIDE,
  WHEEL_SIDE_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioGroupWheelSide';
import {
  RadioGroupWheelSideForSure,
  WHEEL_SIDE_FACTORY_INSTALLED_COMPLEMENTARY,
  WHEEL_SIDE_FOR_SURE,
} from '@/features/v2/vehicle-damage/components/RadioGroupWheelSideForSure';
import {
  AGREEMENT_OR_SETTLEMENT_CONCLUDED,
  AGREEMENT_OR_SETTLEMENT_CONCLUDED_NAME_COMPLEMENTARY,
  SelectAgreementOrSettlementConcluded,
} from '@/features/v2/vehicle-damage/components/SelectAgreementOrSettlementConcluded';
import {
  CAR_CONDITION,
  CAR_CONDITION_NAME_COMPLEMENTARY,
  SelectCarCondition,
} from '@/features/v2/vehicle-damage/components/SelectCarCondition';
import {
  INSURANCE_COMPANY_NAME_COMPLEMENTARY,
  SelectInsuranceCompany,
} from '@/features/v2/vehicle-damage/components/SelectInsuranceCompany';
import { VehicleDamageSchemaDeadEndsStepOneComplementary } from '@/features/v2/vehicle-damage/summarySchema';
import { EnumLike } from '@/types';

import { useLingui } from '@lingui/react';

export const FullAC = () => {
  const { i18n } = useLingui();
  const { step } = useStepper();

  if (step < 4) return null;

  return (
    <>
      <Card>
        <RadioGroupAccidentReportedToInsurer isComplementary />
        <ConditionalRender
          when={(val) =>
            val[ACCIDENT_REPORTED_TO_INSURER_NAME_COMPLEMENTARY] ===
              ACCIDENT_REPORTED_TO_INSURER_VALUES.YES_MY_INSURE ||
            val[ACCIDENT_REPORTED_TO_INSURER_NAME_COMPLEMENTARY] ===
              ACCIDENT_REPORTED_TO_INSURER_VALUES.YES_PERPETRATOR
          }
        >
          <SelectInsuranceCompany isComplementary />
        </ConditionalRender>
      </Card>
      <ConditionalRender
        when={(val) => val[INSURANCE_COMPANY_NAME_COMPLEMENTARY]}
      >
        <Card
          isDeadEnd={
            VehicleDamageSchemaDeadEndsStepOneComplementary
              .radioGroupVehicleValueBeforeCrash.condition
          }
        >
          <RadioGroupVehicleValueBeforeCrash isComplementary />
          <CardDeadEndHint variant="primary">
            <Hint.Title>
              {i18n.t(
                'Niestety, nie możemy podjąć dalszych działań w Twojej sprawie'
              )}
            </Hint.Title>
            <Hint.Description className="caption-large font-jakarta lg:text-xs">
              {i18n.t(
                'W przypadku pojazdów wycenianych przed szkodą całkowitą na kwotę poniżej 10 000 PLN, nasze możliwości działania są ograniczone. Uprzejmie prosimy o zakończenie formularza, aby uniknąć konieczności kontaktu telefonicznego.'
              )}
            </Hint.Description>
          </CardDeadEndHint>
        </Card>
        <ConditionalRender
          when={(val) =>
            val[VEHICLE_VALUE_BEFORE_CRASH_NAME_COMPLEMENTARY] ===
            VEHICLE_VALUE_BEFORE_CRASH.UPPER_10k
          }
        >
          <Card>
            <RadioGroupAccidentInPoland isComplementary />
          </Card>
        </ConditionalRender>

        <ConditionalRender
          when={(val) => val[ACCIDENT_IN_POLAND_NAME_COMPLEMENTARY]}
        >
          <Card
            isDeadEnd={
              VehicleDamageSchemaDeadEndsStepOneComplementary.carSold.condition
            }
          >
            <SelectCarCondition isComplementary />
            <ConditionalRender
              when={(val) =>
                val[CAR_CONDITION_NAME_COMPLEMENTARY] ===
                  CAR_CONDITION.SOLD_REPAIRED ||
                val[CAR_CONDITION_NAME_COMPLEMENTARY] ===
                  CAR_CONDITION.SOLD_DAMAGED
              }
            >
              <div className={'flex flex-col gap-3 lg:grid lg:grid-cols-2 '}>
                <InputVehicleSoldPrice isComplementary />
              </div>
            </ConditionalRender>
            <CardDeadEndHint variant="primary">
              <Hint.Title>
                {i18n.t('Twoja sytuacja jest dla nas szczególnie istotna')}
              </Hint.Title>
              <Hint.Description className="caption-large lg:text-xs">
                {i18n.t(
                  'Przygotuj swoją umowę sprzedaży pojazdu, która jest kluczowym dokumentem w Twojej sprawie i wymaga naszej weryfikacji. W tym celu skontaktujemy się z Tobą wkrótce telefonicznie, aby omówić szczegóły. Do usłyszenia!'
                )}
              </Hint.Description>
            </CardDeadEndHint>
          </Card>
          <ConditionalRender
            when={(val) =>
              val[CAR_CONDITION_NAME_COMPLEMENTARY] ===
                CAR_CONDITION.REPAIRED ||
              val[CAR_CONDITION_NAME_COMPLEMENTARY] ===
                CAR_CONDITION.GOING_TO_BE_REPAIRED
            }
          >
            <Card
              isDeadEnd={
                VehicleDamageSchemaDeadEndsStepOneComplementary
                  .wheelNotOnTheLeft.condition
              }
            >
              <RadioGroupWheelSide isComplementary />
              <ConditionalRender
                when={(val) =>
                  val[WHEEL_SIDE_NAME_COMPLEMENTARY] === WHEEL_SIDE.LEFT
                }
              >
                <RadioGroupWheelSideForSure isComplementary />
              </ConditionalRender>
              <CardDeadEndHint variant="primary">
                <Hint.Title>
                  {i18n.t('Twoja sytuacja jest dla nas szczególnie istotna')}
                </Hint.Title>
                <Hint.Description className="caption-large lg:text-xs">
                  {i18n.t(
                    'Chcielibyśmy zadać Ci kilka pytań, zanim przejdziemy dalej. W tym celu skontaktujemy się z Tobą wkrótce telefonicznie, aby omówić szczegóły. Do usłyszenia!'
                  )}
                </Hint.Description>
              </CardDeadEndHint>
            </Card>
            <ConditionalRender
              when={(val) =>
                val[WHEEL_SIDE_NAME_COMPLEMENTARY] === WHEEL_SIDE.LEFT &&
                val[WHEEL_SIDE_FACTORY_INSTALLED_COMPLEMENTARY] ===
                  WHEEL_SIDE_FOR_SURE.YES
              }
            >
              <Card
                isDeadEnd={
                  VehicleDamageSchemaDeadEndsStepOneComplementary
                    .fullAgreementOrSettlementConcluded.condition
                }
              >
                <RadioGroupInsurerPayCompensation isComplementary />
                <ConditionalRender
                  when={(val) =>
                    !!val[INSURER_PAY_COMPENSATION_NAME_COMPLEMENTARY]
                  }
                >
                  <RadioGroupAgreementOrSettlement isComplementary />
                  <ConditionalRender
                    when={(val) =>
                      val[AGREEMENT_OR_SETTLEMENT_NAME_COMPLEMENTARY] ===
                      AGREEMENT_OR_SETTLEMENT.YES
                    }
                  >
                    <SelectAgreementOrSettlementConcluded isComplementary />
                  </ConditionalRender>
                </ConditionalRender>
                <CardDeadEndHint variant="primary">
                  <Hint.Title>
                    {i18n.t(
                      'Niestety, nie możemy podjąć dalszych działań w Twojej sprawie'
                    )}
                  </Hint.Title>
                  <Hint.Description>
                    <p>
                      {i18n.t(
                        'Zawarcie ugody na koszty naprawy oznacza, że doszło do porozumienia w sprawie odszkodowania za poniesioną szkodę. Ugodę traktuje się jako pełne i ostateczne zadowolenie z kwoty, co wyklucza możliwość dochodzenia dodatkowych środków.'
                      )}
                    </p>
                    <p>
                      {i18n.t(
                        'Jeśli nie zawarto ugody, a ubezpieczyciel wypłacił Ci tylko kwotę bezsporną z kosztorysu, zmień odpowiedź i przejdź do kolejnego etapu.'
                      )}
                    </p>
                  </Hint.Description>
                </CardDeadEndHint>
              </Card>
              <ConditionalRender
                when={(val) =>
                  (!!val[INSURER_PAY_COMPENSATION_NAME_COMPLEMENTARY] &&
                    val[AGREEMENT_OR_SETTLEMENT_NAME_COMPLEMENTARY] ===
                      AGREEMENT_OR_SETTLEMENT.NO) ||
                  (!!val[INSURER_PAY_COMPENSATION_NAME_COMPLEMENTARY] &&
                    val[AGREEMENT_OR_SETTLEMENT_NAME_COMPLEMENTARY] ===
                      AGREEMENT_OR_SETTLEMENT.YES &&
                    !!val[AGREEMENT_OR_SETTLEMENT_CONCLUDED_NAME_COMPLEMENTARY]
                      ?.length &&
                    !val[
                      AGREEMENT_OR_SETTLEMENT_CONCLUDED_NAME_COMPLEMENTARY
                    ]?.some(
                      (item: EnumLike) =>
                        item.value ===
                        AGREEMENT_OR_SETTLEMENT_CONCLUDED.VEHICLE_REPAIR_COSTS
                    ))
                }
              >
                <ComplementarySurveyThree />
              </ConditionalRender>
            </ConditionalRender>
          </ConditionalRender>
        </ConditionalRender>
      </ConditionalRender>
    </>
  );
};
